import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import ClinicAnalytics from "@/components/shared/ClinicAnalytics";
import api from "@/api";
import { toast } from "sonner";
import {
  X, RefreshCw, Key, Filter,
  Stethoscope, Heart,
  MapPin, Ruler, Cake, Play, Trash2, ListChecks,
  AlertTriangle, MessageSquare
} from "lucide-react";
import QuestionnaireEditor from "@/components/education/QuestionnaireEditor";
import RdvConnectorAdminView from "@/components/admin/RdvConnectorAdminView";
import ProtocolsCatalogTab from "@/components/admin/ProtocolsCatalogTab";
import { getItems } from "@/utils/apiHelpers";
import {
  CreatePatientModal, EditPatientModal,
  CreateUserModal, EditUserModal, ResetPasswordModal, EducationContentModal
} from "@/components/admin/modals/ClinicAdminModals";
import ReminderTemplatesEditor from "@/components/admin/clinic/ReminderTemplatesEditor";
import ClinicPerformanceTab from "@/components/admin/clinic/ClinicPerformanceTab";
import ClinicOverviewTab from "@/components/admin/clinic/ClinicOverviewTab";
import ClinicAdminTabs from "@/components/admin/clinic/ClinicAdminTabs";
import ClinicKpiHeader from "@/components/admin/clinic/ClinicKpiHeader";
import ClinicPatientsTab from "@/components/admin/clinic/ClinicPatientsTab";
import ClinicTeamTab from "@/components/admin/clinic/ClinicTeamTab";
import ClinicEducationTab from "@/components/admin/clinic/ClinicEducationTab";

export default function ClinicAdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [kpi, setKpi] = useState(null);
  const [pendingMatch, setPendingMatch] = useState({ toValidate: 0, unmatched: 0, total: 0 });
  const [users, setUsers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [secretaries, setSecretaries] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [businessKpi, setBusinessKpi] = useState(null);
  const [businessPeriod, setBusinessPeriod] = useState("30d");
  
  // Education content state
  const [educationContents, setEducationContents] = useState([]);
  const [showCreateContent, setShowCreateContent] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [editingQuestionnaire, setEditingQuestionnaire] = useState(null);
  
  // Modal states
  const [showCreatePatient, setShowCreatePatient] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditPatient, setShowEditPatient] = useState(null);
  const [showEditUser, setShowEditUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [patientAccessCode, setPatientAccessCode] = useState(null);
  
  // Education filter
  const [eduStatusFilter, setEduStatusFilter] = useState("ALL");
  // Sort state
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  // Delete modal (patients)
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteTyped, setDeleteTyped] = useState("");
  // Delete modal (users)
  const [deleteUserConfirm, setDeleteUserConfirm] = useState(null);
  const [deleteUserTyped, setDeleteUserTyped] = useState("");
  // Pagination totals (for tab badges)
  const [patientTotal, setPatientTotal] = useState(0);
  // Filters
  const [patientFilter, setPatientFilter] = useState({ status: "", search: "" });
  const [userFilter, setUserFilter] = useState({ role: "", search: "" });
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [showInactivePatients, setShowInactivePatients] = useState(false);
  // Reset clinic (test data wipe) modal
  const [showResetClinic, setShowResetClinic] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetArchive, setResetArchive] = useState(true);
  const [resetLoading, setResetLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [kpiRes, usersRes, patientsRes, doctorsRes, nursesRes, secretariesRes, eduRes, pendingRes] = await Promise.all([
        api.get("/kpi/clinic").catch(() => ({ data: {} })),
        api.get("/users?includeInactive=true").catch(() => ({ data: [] })),
        api.get("/patients?includeInactive=true&limit=500").catch(() => ({ data: { items: [] } })),
        api.get("/users/by-role/MEDECIN").catch(() => ({ data: [] })),
        api.get("/users/by-role/IDE").catch(() => ({ data: [] })),
        api.get("/users/by-role/SECRETAIRE_MEDICAL").catch(() => ({ data: [] })),
        api.get("/education/admin/contents").catch(() => ({ data: [] })),
        api.get("/appointments/connector/pending/count").catch(() => ({ data: { toValidate: 0, unmatched: 0, total: 0 } })),
      ]);
      setKpi(kpiRes.data);
      setUsers(usersRes.data);
      setPatients(getItems(patientsRes.data));
      setPatientTotal(patientsRes.data?.total || getItems(patientsRes.data).length);
      setDoctors(doctorsRes.data);
      setNurses(nursesRes.data);
      setSecretaries(secretariesRes.data || []);
      setEducationContents(eduRes.data || []);
      setPendingMatch(pendingRes.data || { toValidate: 0, unmatched: 0, total: 0 });
      // Fetch business KPIs
      try {
        const bizRes = await api.get(`/kpi/business?period=${businessPeriod}`);
        setBusinessKpi(bizRes.data);
      } catch {}
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      api.get(`/kpi/business?period=${businessPeriod}`).then(r => setBusinessKpi(r.data)).catch(() => {});
    }
  }, [businessPeriod]);


  // Filter functions: explicit view mode (active | archived), not a toggle
  const patientView = showInactivePatients ? "archived" : "active";
  const filteredPatients = patients.filter(p => {
    if (p.isDeleted) return false;
    // In "active" view: only show active patients
    // In "archived" view: only show archived (isActive === false)
    const isArchived = p.isActive === false;
    if (patientView === "active" && isArchived) return false;
    if (patientView === "archived" && !isArchived) return false;
    if (patientFilter.status && p.status !== patientFilter.status) return false;
    if (patientFilter.search) {
      const search = patientFilter.search.toLowerCase();
      if (!`${p.firstName} ${p.lastName} ${p.phone}`.toLowerCase().includes(search)) return false;
    }
    return true;
  }).sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    switch (sortBy) {
      case "name":
        return dir * (`${a.firstName} ${a.lastName}`).localeCompare(`${b.firstName} ${b.lastName}`, "fr");
      case "lastSeen":
        return dir * ((a.lastSeenAt || "") > (b.lastSeenAt || "") ? 1 : -1);
      case "status":
        const order = { CRITICAL: 0, OPEN_ALERT: 1, TO_MONITOR: 2, STABLE: 3, HOSPITALIZED: 4 };
        return dir * ((order[a.status] ?? 99) - (order[b.status] ?? 99));
      default:
        return 0;
    }
  });

  const activePatientCount = patients.filter(p => p.isActive !== false && !p.isDeleted).length;
  const inactivePatientCount = patients.filter(p => p.isActive === false && !p.isDeleted).length;

  const filteredUsers = users.filter(u => {
    // Filter active/inactive
    if (!showInactiveUsers && !u.isActive) return false;
    if (userFilter.role && u.role !== userFilter.role) return false;
    if (userFilter.search) {
      const search = userFilter.search.toLowerCase();
      if (!`${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  const inactiveCount = users.filter(u => !u.isActive).length;

  const handleArchivePatient = async (patientId) => {
    if (!confirm("Voulez-vous vraiment archiver ce patient ? Vous pourrez le restaurer depuis la liste des archivés.")) return;
    try {
      await api.patch(`/patients/${patientId}/archive`);
      toast.success("Patient archivé");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleRestorePatient = async (patientId) => {
    try {
      await api.patch(`/patients/${patientId}/restore`);
      toast.success("Patient restauré");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la restauration");
    }
  };

  const handleResetPatientAccess = async (patient) => {
    if (!confirm(`Générer un nouveau code d’accès pour ${patient.firstName} ${patient.lastName} ? L’ancien code patient sera remplacé.`)) return;
    try {
      const res = await api.post(`/patients/${patient.id}/reset-access-code`);
      setPatientAccessCode({
        patientName: `${patient.firstName} ${patient.lastName}`.trim(),
        phone: patient.phone,
        code: res.data.activationCode,
        expiresAt: res.data.expiresAt,
      });
      toast.success("Nouveau code patient généré");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de générer le code");
    }
  };

  const handleGdprDelete = async (patientId) => {
    try {
      await api.delete(`/patients/${patientId}/gdpr-delete`);
      toast.success("Patient supprime definitivement");
      setDeleteConfirm(null);
      setDeleteTyped("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleResetClinicData = async () => {
    if (resetConfirm !== "RESET") {
      toast.error("Tape RESET pour confirmer");
      return;
    }
    setResetLoading(true);
    try {
      const { data } = await api.post("/patients/reset-clinic-data", {
        clinicId: user?.clinicId,
        confirm: "RESET",
        archiveSnapshot: resetArchive,
      });
      toast.success(`${data.deleted_count} patient(s) supprimes${resetArchive ? ` (${data.archived_snapshots} snapshots anonymises conserves)` : ""}`);
      setShowResetClinic(false);
      setResetConfirm("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du reset");
    } finally {
      setResetLoading(false);
    }
  };

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!confirm("Voulez-vous vraiment désactiver cet utilisateur ? Vous pourrez le restaurer depuis la liste des archivés.")) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success("Utilisateur désactivé");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      await api.patch(`/users/${userId}/restore`);
      toast.success("Utilisateur restauré");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la restauration");
    }
  };

  const handlePermanentDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}/permanent`);
      toast.success("Utilisateur supprimé définitivement");
      setDeleteUserConfirm(null);
      setDeleteUserTyped("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-slate-500">Gestion des patients et de l'équipe médicale</p>
          <button
            onClick={fetchData}
            disabled={loading}
            data-testid="clinic-admin-refresh-btn"
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>

        {/* KPIs */}
        <ClinicKpiHeader
          kpi={kpi}
          pendingMatch={pendingMatch}
          onOpenPending={() => navigate("/admin/appointments-validation")}
        />

        {/* Tabs */}
        <ClinicAdminTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          patientTotal={patientTotal}
          users={users}
          educationContents={educationContents}
        />

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <ClinicOverviewTab
            kpi={kpi}
            users={users}
            onOpenCreatePatient={() => setShowCreatePatient(true)}
            onOpenCreateUser={() => setShowCreateUser(true)}
            onGoToPerformance={() => setActiveTab("performance")}
            onGoToTeamRole={(roleValue) => { setActiveTab("team"); setUserFilter(f => ({ ...f, role: roleValue })); }}
          />
        )}

        {/* Performance Tab — KPIs Business */}
        {activeTab === "performance" && (
          <ClinicPerformanceTab
            businessKpi={businessKpi}
            businessPeriod={businessPeriod}
            onPeriodChange={setBusinessPeriod}
          />
        )}

        {/* Legacy Analytics Tab */}
        {activeTab === "analytics" && (
          <ClinicAnalytics clinicId={user?.clinicId} clinicName={null} />
        )}

        {/* Patients Tab */}
        {activeTab === "patients" && (
          <ClinicPatientsTab
            filteredPatients={filteredPatients}
            activePatientCount={activePatientCount}
            inactivePatientCount={inactivePatientCount}
            patientFilter={patientFilter}
            onPatientFilterChange={setPatientFilter}
            showInactivePatients={showInactivePatients}
            onShowInactiveChange={setShowInactivePatients}
            sortBy={sortBy}
            sortDir={sortDir}
            onToggleSort={toggleSort}
            onOpenResetClinic={() => setShowResetClinic(true)}
            onOpenCreate={() => setShowCreatePatient(true)}
            onRestore={handleRestorePatient}
            onResetAccessCode={handleResetPatientAccess}
            onEdit={(patient) => setShowEditPatient(patient)}
            onArchive={handleArchivePatient}
            onDeleteRequest={(patient) => { setDeleteConfirm(patient); setDeleteTyped(""); }}
          />
        )}

        {/* Team Tab */}
        {activeTab === "team" && (
          <ClinicTeamTab
            filteredUsers={filteredUsers}
            inactiveCount={inactiveCount}
            userFilter={userFilter}
            onUserFilterChange={setUserFilter}
            showInactiveUsers={showInactiveUsers}
            onToggleInactive={() => setShowInactiveUsers(!showInactiveUsers)}
            onOpenCreate={() => setShowCreateUser(true)}
            onEdit={(u) => setShowEditUser(u)}
            onResetPassword={(u) => setShowResetPassword(u)}
            onDeactivate={handleDeactivateUser}
            onRestore={handleRestoreUser}
            onDeleteRequest={(u) => { setDeleteUserConfirm(u); setDeleteUserTyped(""); }}
          />
        )}

        {/* Education Tab */}
        {activeTab === "education" && (
          <ClinicEducationTab
            educationContents={educationContents}
            eduStatusFilter={eduStatusFilter}
            onStatusFilterChange={setEduStatusFilter}
            onOpenCreate={() => setShowCreateContent(true)}
            onEdit={(content) => setEditingContent(content)}
            onRefresh={fetchData}
          />
        )}
      </div>

        {activeTab === "reminders" && <ReminderTemplatesEditor />}
        {activeTab === "protocols" && <ProtocolsCatalogTab currentUserRole="ADMIN_CLINIQUE" />}

        {activeTab === "connector" && <RdvConnectorAdminView />}


      {/* Education Content Modal */}
      {(showCreateContent || editingContent) && (
        <EducationContentModal
          content={editingContent}
          onClose={() => { setShowCreateContent(false); setEditingContent(null); }}
          onSaved={() => { setShowCreateContent(false); setEditingContent(null); fetchData(); }}
        />
      )}

      {/* Questionnaire Editor Modal */}
      {editingQuestionnaire && (
        <QuestionnaireEditor
          contentId={editingQuestionnaire.id}
          contentTitle={editingQuestionnaire.title}
          onClose={() => setEditingQuestionnaire(null)}
          onSaved={() => setEditingQuestionnaire(null)}
        />
      )}

      {/* Modals */}
      {showCreatePatient && (
        <CreatePatientModal
          doctors={doctors}
          nurses={nurses}
          secretaries={secretaries}
          onClose={() => setShowCreatePatient(false)}
          onCreated={() => { setShowCreatePatient(false); fetchData(); }}
        />
      )}
      
      {showEditPatient && (
        <EditPatientModal
          patient={showEditPatient}
          doctors={doctors}
          nurses={nurses}
          secretaries={secretaries}
          onClose={() => setShowEditPatient(null)}
          onUpdated={() => { setShowEditPatient(null); fetchData(); }}
        />
      )}
      
      {showCreateUser && (
        <CreateUserModal
          onClose={() => setShowCreateUser(false)}
          onCreated={() => { setShowCreateUser(false); fetchData(); }}
        />
      )}
      
      {showEditUser && (
        <EditUserModal
          user={showEditUser}
          onClose={() => setShowEditUser(null)}
          onUpdated={() => { setShowEditUser(null); fetchData(); }}
        />
      )}
      
      {showResetPassword && (
        <ResetPasswordModal
          user={showResetPassword}
          onClose={() => setShowResetPassword(null)}
          onReset={() => { setShowResetPassword(null); toast.success("Mot de passe réinitialisé"); }}
        />
      )}

      {patientAccessCode && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" data-testid="patient-access-code-modal">
            <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mb-4">
              <Key className="w-6 h-6 text-sky-700" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Code d’accès patient</h2>
            <p className="text-sm text-slate-500 mt-1">{patientAccessCode.patientName} · {patientAccessCode.phone}</p>
            <div className="mt-5 rounded-2xl border border-sky-200 bg-sky-50 p-5 text-center">
              <p className="text-xs font-semibold text-sky-800 uppercase tracking-wide">À remettre en clinique</p>
              <p className="text-4xl font-black tracking-widest text-sky-900 mt-2" data-testid="patient-access-code-value">{patientAccessCode.code}</p>
              <p className="text-xs text-sky-700 mt-3">Le patient devra créer son code personnel à la première connexion.</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => window.print()}
                className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold"
                data-testid="print-patient-access-code-button"
              >
                Imprimer
              </button>
              <button
                type="button"
                onClick={() => setPatientAccessCode(null)}
                className="flex-1 py-2.5 bg-sky-700 text-white rounded-xl text-sm font-semibold"
                data-testid="close-patient-access-code-button"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GDPR Delete Confirmation Modal */}
      {/* Reset Clinic Data Modal */}
      {showResetClinic && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" data-testid="reset-clinic-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reset donnees de test</h3>
                <p className="text-sm text-slate-500">Action irreversible</p>
              </div>
            </div>
            <p className="text-sm text-slate-700 mb-3">
              Cette action va supprimer <b>TOUS les patients de la clinique</b> (actifs et archives) ainsi que leurs donnees associees : check-ins, alertes, messages, documents, rendez-vous, consentements.
            </p>
            <label className="flex items-center gap-2 mb-4 text-sm">
              <input
                type="checkbox"
                checked={resetArchive}
                onChange={(e) => setResetArchive(e.target.checked)}
                data-testid="reset-archive-snapshot"
              />
              <span>Conserver un snapshot anonymise dans <code>archivedPatients</code> (recommande pour statistiques)</span>
            </label>
            <label className="block mb-4">
              <span className="text-xs text-slate-600 block mb-1">Tape <b>RESET</b> pour confirmer</span>
              <input
                type="text"
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="RESET"
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                data-testid="reset-confirm-input"
              />
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowResetClinic(false); setResetConfirm(""); }}
                disabled={resetLoading}
                data-testid="reset-cancel-btn"
                className="flex-1 py-2 border rounded-lg text-sm"
              >
                Annuler
              </button>
              <button
                onClick={handleResetClinicData}
                disabled={resetLoading || resetConfirm !== "RESET"}
                data-testid="reset-confirm-btn"
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {resetLoading ? "Suppression..." : "Supprimer tout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="gdpr-delete-modal">
            <div className="flex items-center gap-3 text-red-600">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold">Suppression définitive</h3>
            </div>
            <div className="space-y-2 text-sm text-slate-600">
              <p>Vous allez supprimer définitivement <span className="font-semibold text-slate-800">{deleteConfirm.firstName} {deleteConfirm.lastName}</span>.</p>
              <p>Conformément à la CNDP, cette action va :</p>
              <ul className="list-disc pl-5 space-y-1 text-slate-500">
                <li>Anonymiser les données personnelles (nom, téléphone)</li>
                <li>Supprimer les sessions, documents et messages</li>
                <li>Conserver les données cliniques anonymisées (obligation légale)</li>
              </ul>
              <p className="font-semibold text-red-600 mt-3">Cette action est irréversible.</p>
            </div>
            <div>
              <label className="text-sm text-slate-500 block mb-1">Tapez <span className="font-mono font-semibold text-red-600">SUPPRIMER</span> pour confirmer</label>
              <input
                type="text"
                value={deleteTyped}
                onChange={e => setDeleteTyped(e.target.value)}
                data-testid="gdpr-delete-confirm-input"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                placeholder="SUPPRIMER"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handleGdprDelete(deleteConfirm.id)}
                disabled={deleteTyped !== "SUPPRIMER"}
                data-testid="gdpr-delete-confirm-btn"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Permanent Delete Modal */}
      {deleteUserConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDeleteUserConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="user-delete-modal">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-rose-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Suppression definitive</h3>
            </div>
            <div className="text-sm text-slate-600 space-y-2">
              <p>Vous allez supprimer definitivement <span className="font-semibold text-slate-800">{deleteUserConfirm.firstName} {deleteUserConfirm.lastName}</span> ({deleteUserConfirm.role}).</p>
              <p className="text-slate-500">Cette action est <span className="text-rose-600 font-semibold">irreversible</span>. Le compte sera supprime, ses messages anonymises, et ses patients desassignes.</p>
            </div>
            <div>
              <label className="text-sm text-slate-500 block mb-1">Tapez <span className="font-mono font-semibold text-red-600">SUPPRIMER</span> pour confirmer</label>
              <input
                type="text"
                value={deleteUserTyped}
                onChange={e => setDeleteUserTyped(e.target.value)}
                data-testid="user-delete-confirm-input"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                placeholder="SUPPRIMER"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteUserConfirm(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handlePermanentDeleteUser(deleteUserConfirm.id)}
                disabled={deleteUserTyped !== "SUPPRIMER"}
                data-testid="user-delete-confirm-btn"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Supprimer definitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </ProfessionalLayout>
  );
}

